import React, { useState, useEffect } from 'react';
import {
  Activity, Users, TrendingUp, DollarSign, CreditCard, MessageSquare,
  BarChart3, PieChart, Calendar, Clock, Monitor, Smartphone, Globe,
  RefreshCw, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency, getMonthlyRevenue } from '../utils/commissionCalc';
import { getMonthlyEquivalents, SEAT_PRICE } from '../utils/tierPricing';

const TIER_MONTHLY_PRICES = getMonthlyEquivalents();

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Usage stats
  const [usageStats, setUsageStats] = useState({
    onlineNow: 0,
    active24h: 0,
    active7d: 0,
    active30d: 0,
    totalUsers: 0,
    totalCompanies: 0
  });

  const [moduleUsage, setModuleUsage] = useState([]);
  const [deviceStats, setDeviceStats] = useState({ desktop: 0, mobile: 0, tablet: 0 });

  // Financial stats
  const [financialStats, setFinancialStats] = useState({
    mrr: 0,
    arr: 0,
    activeSubscriptions: 0,
    trialingCompanies: 0,
    canceledCompanies: 0,
    seatRevenue: 0,
    totalSeats: 0,
    messagingRevenue: 0,
    messagingCompanies: 0,
    invoicesPaid: 0,
    invoicesTotal: 0,
    invoicesPending: 0,
    invoicesPendingAmount: 0,
    processingVolume30d: 0
  });

  const [tierBreakdown, setTierBreakdown] = useState([]);
  const [billingBreakdown, setBillingBreakdown] = useState([]);

  // Revenue forecast
  const [forecast, setForecast] = useState({
    currentMrr: 0,
    projectedMrr3m: 0,
    projectedMrr6m: 0,
    projectedMrr12m: 0,
    trialConversionRate: 0,
    monthlyGrowthRate: 0,
    activeCount: 0,
    trialingCount: 0,
    canceledCount: 0,
    totalEverTrialed: 0
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadUsageStats(),
        loadFinancialStats()
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadUsageStats = async () => {
    // Fetch presence data for activity stats
    const { data: presenceData } = await supabase
      .from('user_presence')
      .select('user_id, current_page, last_seen, user_agent');

    const { data: users } = await supabase
      .from('user_profiles')
      .select('id');

    const { data: companies } = await supabase
      .from('companies')
      .select('id');

    const now = new Date();
    const fifteenMinsAgo = new Date(now - 15 * 60 * 1000);
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    const presence = presenceData || [];

    // Count active users by time period
    const onlineNow = presence.filter(p => new Date(p.last_seen) > fifteenMinsAgo).length;
    const active24h = new Set(presence.filter(p => new Date(p.last_seen) > oneDayAgo).map(p => p.user_id)).size;
    const active7d = new Set(presence.filter(p => new Date(p.last_seen) > sevenDaysAgo).map(p => p.user_id)).size;
    const active30d = new Set(presence.filter(p => new Date(p.last_seen) > thirtyDaysAgo).map(p => p.user_id)).size;

    setUsageStats({
      onlineNow,
      active24h,
      active7d,
      active30d,
      totalUsers: users?.length || 0,
      totalCompanies: companies?.length || 0
    });

    // Module usage (current page distribution)
    const moduleCount = {};
    presence.forEach(p => {
      const page = p.current_page || 'unknown';
      moduleCount[page] = (moduleCount[page] || 0) + 1;
    });

    const moduleList = Object.entries(moduleCount)
      .map(([name, count]) => ({ name: formatModuleName(name), count, key: name }))
      .sort((a, b) => b.count - a.count);

    setModuleUsage(moduleList);

    // Device stats from user agent
    let desktop = 0, mobile = 0, tablet = 0;
    presence.forEach(p => {
      const ua = (p.user_agent || '').toLowerCase();
      if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
        if (ua.includes('ipad') || ua.includes('tablet')) {
          tablet++;
        } else {
          mobile++;
        }
      } else {
        desktop++;
      }
    });
    setDeviceStats({ desktop, mobile, tablet });
  };

  const loadFinancialStats = async () => {
    // Fetch companies with subscription data
    const { data: companies } = await supabase
      .from('companies')
      .select('id, tier, billing_interval, subscription_status, purchased_seats, messaging_enabled');

    const companiesList = companies || [];

    // Calculate MRR from active subscriptions
    let mrr = 0;
    let seatRevenue = 0;
    let totalSeats = 0;
    let activeCount = 0;
    let trialingCount = 0;
    let canceledCount = 0;
    let messagingCount = 0;

    const tierCounts = {};
    const billingCounts = {};

    companiesList.forEach(c => {
      if (c.subscription_status === 'active') {
        activeCount++;

        // Base tier revenue
        const tier = c.tier || 'pro';
        const interval = c.billing_interval || 'monthly';
        const tierPrice = TIER_MONTHLY_PRICES[tier]?.[interval] || TIER_MONTHLY_PRICES.pro.monthly;
        mrr += tierPrice;

        // Tier breakdown
        tierCounts[tier] = (tierCounts[tier] || 0) + 1;
        billingCounts[interval] = (billingCounts[interval] || 0) + 1;

        // Seat revenue
        const seats = c.purchased_seats || 0;
        if (seats > 0) {
          totalSeats += seats;
          seatRevenue += seats * SEAT_PRICE;
        }
      } else if (c.subscription_status === 'trialing') {
        trialingCount++;
      } else if (c.subscription_status === 'canceled') {
        canceledCount++;
      }

      if (c.messaging_enabled) {
        messagingCount++;
      }
    });

    // Messaging revenue estimate ($25/mo per messaging company)
    const messagingRevenue = messagingCount * 25;

    // Total MRR includes seats and messaging
    const totalMrr = mrr + seatRevenue + messagingRevenue;

    // Invoice stats
    const { data: invoices } = await supabase
      .from('invoices')
      .select('id, total, status, paid_at');

    const invoiceList = invoices || [];
    const paidInvoices = invoiceList.filter(i => i.status === 'paid');
    const pendingInvoices = invoiceList.filter(i => i.status === 'sent' || i.status === 'pending');

    const invoicesPaid = paidInvoices.length;
    const invoicesTotal = paidInvoices.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);
    const invoicesPending = pendingInvoices.length;
    const invoicesPendingAmount = pendingInvoices.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

    // Processing volume last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentPaid = paidInvoices.filter(i =>
      i.paid_at && new Date(i.paid_at) > thirtyDaysAgo
    );
    const processingVolume30d = recentPaid.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

    setFinancialStats({
      mrr: totalMrr,
      arr: totalMrr * 12,
      activeSubscriptions: activeCount,
      trialingCompanies: trialingCount,
      canceledCompanies: canceledCount,
      seatRevenue,
      totalSeats,
      messagingRevenue,
      messagingCompanies: messagingCount,
      invoicesPaid,
      invoicesTotal,
      invoicesPending,
      invoicesPendingAmount,
      processingVolume30d
    });

    // Tier breakdown
    setTierBreakdown(
      Object.entries(tierCounts)
        .map(([tier, count]) => ({ tier: tier.charAt(0).toUpperCase() + tier.slice(1), count }))
        .sort((a, b) => b.count - a.count)
    );

    // Billing breakdown
    setBillingBreakdown(
      Object.entries(billingCounts)
        .map(([interval, count]) => ({ interval: interval.charAt(0).toUpperCase() + interval.slice(1), count }))
        .sort((a, b) => b.count - a.count)
    );

    // Revenue Forecast calculations
    // Total companies that ever trialed = active (converted) + trialing (still in trial) + canceled (churned from trial or active)
    const totalEverTrialed = activeCount + trialingCount + canceledCount;
    const trialConversionRate = totalEverTrialed > 0 ? activeCount / totalEverTrialed : 0;

    // Estimate new trials per month based on current trialing count (proxy for monthly acquisition)
    const estimatedNewTrialsPerMonth = Math.max(trialingCount, 1);

    // Average revenue per active company
    const avgRevenuePerCompany = activeCount > 0 ? totalMrr / activeCount : 0;

    // New MRR per month = new trials * conversion rate * avg revenue
    const newMrrPerMonth = estimatedNewTrialsPerMonth * trialConversionRate * avgRevenuePerCompany;

    // Monthly growth rate as percentage
    const monthlyGrowthRate = totalMrr > 0 ? (newMrrPerMonth / totalMrr) : 0;

    // Project MRR using linear growth (adding newMrrPerMonth each month)
    const projectedMrr3m = totalMrr + (newMrrPerMonth * 3);
    const projectedMrr6m = totalMrr + (newMrrPerMonth * 6);
    const projectedMrr12m = totalMrr + (newMrrPerMonth * 12);

    setForecast({
      currentMrr: totalMrr,
      projectedMrr3m,
      projectedMrr6m,
      projectedMrr12m,
      trialConversionRate,
      monthlyGrowthRate,
      activeCount,
      trialingCount,
      canceledCount,
      totalEverTrialed
    });
  };

  const formatModuleName = (name) => {
    const names = {
      'home': 'Dashboard',
      'today': 'Today',
      'customers': 'Customers',
      'work-orders': 'Work Orders',
      'quotes': 'Quotes',
      'express-job': 'Express Job',
      'settings': 'Settings',
      'planning': 'Calendar',
      'products-pricing': 'Products',
      'team': 'Team',
      'financial': 'Financial',
      'money': 'Money',
      'commissions': 'Commissions',
      'vendors': 'Vendors'
    };
    return names[name] || name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading analytics...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem', boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <BarChart3 size={28} color="#ff6b35" />
            Platform Analytics
          </h1>
          <p style={{ color: '#888' }}>
            Usage statistics and financial overview
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {lastUpdated && (
            <span style={{ color: '#666', fontSize: '0.85rem' }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={loadAnalytics}
            disabled={refreshing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Usage & Activity Section */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#e0e0e0',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Activity size={20} color="#4ecca3" />
          Usage & Activity
        </h2>

        {/* Activity Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <StatCard
            label="Online Now"
            value={usageStats.onlineNow}
            icon={Activity}
            color="#4ecca3"
            subtitle="Active in last 15 min"
          />
          <StatCard
            label="Active 24h"
            value={usageStats.active24h}
            icon={Clock}
            color="#3498db"
            subtitle="Unique users"
          />
          <StatCard
            label="Active 7 Days"
            value={usageStats.active7d}
            icon={Calendar}
            color="#9b59b6"
            subtitle="Unique users"
          />
          <StatCard
            label="Active 30 Days"
            value={usageStats.active30d}
            icon={TrendingUp}
            color="#f39c12"
            subtitle="Unique users"
          />
          <StatCard
            label="Total Users"
            value={usageStats.totalUsers}
            icon={Users}
            color="#e74c3c"
            subtitle="All registered"
          />
          <StatCard
            label="Total Companies"
            value={usageStats.totalCompanies}
            icon={Globe}
            color="#1abc9c"
            subtitle="All accounts"
          />
        </div>

        {/* Module Usage & Device Split */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1.5rem'
        }}>
          {/* Module Usage */}
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#e0e0e0',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <PieChart size={18} color="#9b59b6" />
              Module Usage
            </h3>

            {moduleUsage.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {moduleUsage.slice(0, 8).map((mod, idx) => {
                  const total = moduleUsage.reduce((sum, m) => sum + m.count, 0);
                  const pct = total > 0 ? ((mod.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div
                      key={mod.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <div style={{
                        flex: 1,
                        background: '#242424',
                        borderRadius: '4px',
                        height: '28px',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${pct}%`,
                          background: `hsl(${260 - idx * 20}, 60%, 50%)`,
                          borderRadius: '4px'
                        }} />
                        <div style={{
                          position: 'relative',
                          padding: '0 0.75rem',
                          lineHeight: '28px',
                          fontSize: '0.85rem',
                          color: '#e0e0e0',
                          fontWeight: '500'
                        }}>
                          {mod.name}
                        </div>
                      </div>
                      <div style={{
                        width: '60px',
                        textAlign: 'right',
                        fontSize: '0.85rem',
                        color: '#888',
                        fontFamily: 'monospace'
                      }}>
                        {mod.count} ({pct}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#666', fontSize: '0.9rem' }}>No module data available</div>
            )}
          </div>

          {/* Device Distribution */}
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#e0e0e0',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Monitor size={18} color="#3498db" />
              Device Distribution
            </h3>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <DeviceCard icon={Monitor} label="Desktop" count={deviceStats.desktop} color="#3498db" />
              <DeviceCard icon={Smartphone} label="Mobile" count={deviceStats.mobile} color="#4ecca3" />
              <DeviceCard icon={Monitor} label="Tablet" count={deviceStats.tablet} color="#f39c12" />
            </div>
          </div>
        </div>
      </div>

      {/* Financial Overview Section */}
      <div>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#e0e0e0',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <DollarSign size={20} color="#ff6b35" />
          Financial Overview
        </h2>

        {/* Revenue Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <StatCard
            label="Monthly Recurring Revenue"
            value={formatCurrency(financialStats.mrr)}
            icon={TrendingUp}
            color="#4ecca3"
            subtitle="MRR"
            large
          />
          <StatCard
            label="Annual Run Rate"
            value={formatCurrency(financialStats.arr)}
            icon={Calendar}
            color="#3498db"
            subtitle="ARR (MRR × 12)"
            large
          />
          <StatCard
            label="Active Subscriptions"
            value={financialStats.activeSubscriptions}
            icon={CreditCard}
            color="#9b59b6"
            subtitle={`${financialStats.trialingCompanies} trialing`}
          />
          <StatCard
            label="Processing Volume"
            value={formatCurrency(financialStats.processingVolume30d)}
            icon={DollarSign}
            color="#ff6b35"
            subtitle="Last 30 days"
          />
        </div>

        {/* Revenue Breakdown */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Subscription Tiers */}
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#e0e0e0',
              marginBottom: '1rem'
            }}>
              Subscription Tiers
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {tierBreakdown.map(t => (
                <div
                  key={t.tier}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: '#242424',
                    borderRadius: '8px'
                  }}
                >
                  <span style={{ color: '#e0e0e0', fontWeight: '500' }}>{t.tier}</span>
                  <span style={{
                    background: '#4ecca320',
                    color: '#4ecca3',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {t.count} companies
                  </span>
                </div>
              ))}
              {tierBreakdown.length === 0 && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>No active subscriptions</div>
              )}
            </div>
          </div>

          {/* Billing Intervals */}
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#e0e0e0',
              marginBottom: '1rem'
            }}>
              Billing Intervals
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {billingBreakdown.map(b => (
                <div
                  key={b.interval}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: '#242424',
                    borderRadius: '8px'
                  }}
                >
                  <span style={{ color: '#e0e0e0', fontWeight: '500' }}>{b.interval}</span>
                  <span style={{
                    background: '#3498db20',
                    color: '#3498db',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {b.count} companies
                  </span>
                </div>
              ))}
              {billingBreakdown.length === 0 && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>No billing data</div>
              )}
            </div>
          </div>

          {/* Add-on Revenue */}
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#e0e0e0',
              marginBottom: '1rem'
            }}>
              Add-on Revenue
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                background: '#242424',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ color: '#e0e0e0', fontWeight: '500' }}>Extra Seats</div>
                  <div style={{ color: '#888', fontSize: '0.8rem' }}>{financialStats.totalSeats} seats @ $5/mo</div>
                </div>
                <span style={{ color: '#4ecca3', fontWeight: '600', fontSize: '1.1rem' }}>
                  {formatCurrency(financialStats.seatRevenue)}/mo
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                background: '#242424',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ color: '#e0e0e0', fontWeight: '500' }}>Messaging</div>
                  <div style={{ color: '#888', fontSize: '0.8rem' }}>{financialStats.messagingCompanies} companies</div>
                </div>
                <span style={{ color: '#9b59b6', fontWeight: '600', fontSize: '1.1rem' }}>
                  {formatCurrency(financialStats.messagingRevenue)}/mo
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '1rem'
        }}>
          <StatCard
            label="Invoices Paid"
            value={financialStats.invoicesPaid.toLocaleString()}
            icon={CreditCard}
            color="#4ecca3"
            subtitle={`${formatCurrency(financialStats.invoicesTotal)} total`}
          />
          <StatCard
            label="Invoices Pending"
            value={financialStats.invoicesPending.toLocaleString()}
            icon={Clock}
            color="#f39c12"
            subtitle={`${formatCurrency(financialStats.invoicesPendingAmount)} outstanding`}
          />
        </div>
      </div>

      {/* Revenue Forecast Section */}
      <div style={{ marginTop: '2.5rem' }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: '#e0e0e0',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <TrendingUp size={20} color="#ff6b35" />
          Revenue Forecast
        </h2>

        {/* Projection Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #2a2a2a',
            borderLeft: '4px solid #ff6b35'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>Current MRR</div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#ff6b35',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {formatCurrency(forecast.currentMrr)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>Today</div>
          </div>

          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #2a2a2a',
            borderLeft: '4px solid #4ecca3'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>3-Month Projection</div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#4ecca3',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {formatCurrency(forecast.projectedMrr3m)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
              +{formatCurrency(forecast.projectedMrr3m - forecast.currentMrr)}
            </div>
          </div>

          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #2a2a2a',
            borderLeft: '4px solid #3498db'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>6-Month Projection</div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#3498db',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {formatCurrency(forecast.projectedMrr6m)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
              +{formatCurrency(forecast.projectedMrr6m - forecast.currentMrr)}
            </div>
          </div>

          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #2a2a2a',
            borderLeft: '4px solid #9b59b6'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>12-Month Projection</div>
            <div style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#9b59b6',
              fontFamily: '"JetBrains Mono", monospace'
            }}>
              {formatCurrency(forecast.projectedMrr12m)}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
              +{formatCurrency(forecast.projectedMrr12m - forecast.currentMrr)}
            </div>
          </div>
        </div>

        {/* Assumptions Card */}
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          border: '1px solid #2a2a2a',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#e0e0e0',
            marginBottom: '1rem'
          }}>
            Forecast Assumptions
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div style={{
              padding: '1rem',
              background: '#242424',
              borderRadius: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Trial Conversion Rate</div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#4ecca3',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {(forecast.trialConversionRate * 100).toFixed(1)}%
              </div>
              <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {forecast.activeCount} active / {forecast.totalEverTrialed} total
              </div>
            </div>

            <div style={{
              padding: '1rem',
              background: '#242424',
              borderRadius: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Est. Monthly Growth Rate</div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#ff6b35',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {(forecast.monthlyGrowthRate * 100).toFixed(1)}%
              </div>
              <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Based on current trial pipeline
              </div>
            </div>

            <div style={{
              padding: '1rem',
              background: '#242424',
              borderRadius: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Currently Trialing</div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#f39c12',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {forecast.trialingCount}
              </div>
              <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Companies in trial period
              </div>
            </div>

            <div style={{
              padding: '1rem',
              background: '#242424',
              borderRadius: '8px'
            }}>
              <div style={{ color: '#888', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Churned / Canceled</div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#e74c3c',
                fontFamily: '"JetBrains Mono", monospace'
              }}>
                {forecast.canceledCount}
              </div>
              <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Did not convert or canceled
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: '#ff6b3510',
            border: '1px solid #ff6b3530',
            borderRadius: '8px',
            fontSize: '0.8rem',
            color: '#888',
            lineHeight: '1.5'
          }}>
            Projections use a linear growth model based on current trialing companies as a proxy for monthly acquisition rate,
            multiplied by the historical trial-to-active conversion rate. Actual results may vary based on marketing efforts,
            seasonality, and churn.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, subtitle, large }) {
  return (
    <div style={{
      background: '#1a1a1a',
      borderRadius: '12px',
      padding: large ? '1.75rem' : '1.25rem',
      border: '1px solid #2a2a2a',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '0.75rem'
      }}>
        <Icon size={20} color={color} />
      </div>
      <div style={{
        fontSize: large ? '1.75rem' : '1.5rem',
        fontWeight: '700',
        color: color,
        fontFamily: '"JetBrains Mono", monospace',
        marginBottom: '0.25rem'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '0.85rem',
        color: '#e0e0e0',
        fontWeight: '500'
      }}>
        {label}
      </div>
      {subtitle && (
        <div style={{
          fontSize: '0.75rem',
          color: '#666',
          marginTop: '0.25rem'
        }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function DeviceCard({ icon: Icon, label, count, color }) {
  return (
    <div style={{
      flex: 1,
      background: '#242424',
      borderRadius: '8px',
      padding: '1rem',
      textAlign: 'center'
    }}>
      <Icon size={24} color={color} style={{ marginBottom: '0.5rem' }} />
      <div style={{
        fontSize: '1.5rem',
        fontWeight: '700',
        color: color,
        fontFamily: '"JetBrains Mono", monospace'
      }}>
        {count}
      </div>
      <div style={{ fontSize: '0.8rem', color: '#888' }}>{label}</div>
    </div>
  );
}
